import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { idZodSchema } from "@@/shared/id";
import type {
    ProviderId} from "@@/shared/providers/provider-id";
import {
    providerIdZodSchema,
} from "@@/shared/providers/provider-id";
import type {
    Translatable} from "@@/shared/translations/translation";
import {
    translatableZodSchema,
} from "@@/shared/translations/translation";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type { ProductId} from "../product-id";
import { productIdZodSchema } from "../product-id";
import type { ProductCommandCommon } from "./product-commands-common";

type ProductDuplicateCommand = {
    readonly _id: string;
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _type: "PRODUCT_DUPLICATE_COMMAND";
    /**
     * Product to duplicate
     * @type {ProductId}
     */
    readonly productId: ProductId;
    /**
     * ProviderId of product to duplicate
     * @type {ProviderId}
     */
    readonly providerId: ProviderId;
    /**
     * Target productId of duplicated product. If not provided, a new productId will be generated
     * @type {ProductId}
     */
    readonly targetProductId?: ProductId;
    /**
     * Name of new newly duplicated product, defaults to the old one with a prefix of `Copy of`
     * @type {Translatable}
     */
    readonly targetTitle?: Translatable;
    /**
     * Target duplicate of product to given target provider \
     * If not given, the product will be duplicated to the same provider as it currently belongs to
     * @type {ProviderId}
     */
    readonly targetProviderId?: ProviderId;
    /**
     * When true, the new duplicated product will be paused.
     * If left undefined or false, it will duplicate the state of the original product
     *
     * @type {boolean}
     */
    readonly pause?: boolean;
};

type FactoryParams = Except<
    ProductCommandCommon & ProductDuplicateCommand,
    "_id" | "_type" | "_createdAt"
>;
const productDuplicateCommandFactory = (params: FactoryParams) => {
    const orderRejection: ProductDuplicateCommand = {
        ...params,
        _id: generateId({ prefix: "product_duplicate_command__" }),
        _type: "PRODUCT_DUPLICATE_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return orderRejection;
};

const productDuplicateCommandZodSchema: z.ZodSchema<ProductDuplicateCommand> =
    z.object({
        _id: idZodSchema,
        _type: z.literal("PRODUCT_DUPLICATE_COMMAND"),
        _createdAt: isoAndUnixZodSchema,
        providerId: providerIdZodSchema,
        productId: productIdZodSchema,
        targetTitle: translatableZodSchema.optional(),
        targetProductId: productIdZodSchema.optional(),
        targetProviderId: providerIdZodSchema.optional(),
        pause: z.boolean().optional(),
    });

export { productDuplicateCommandFactory, productDuplicateCommandZodSchema };
export type {
    ProductDuplicateCommand,
    FactoryParams as ProductDuplicateCommandParams,
};
